/**
 * PriceHistoryDrawer — full billed-purchase history for one item.
 *
 * Rendered by Price Explorer when a user clicks "See full purchase history"
 * on any result card. Loads paginated data from
 *   GET /api/price-explorer/history/{icode}
 * and offers a per-supplier filter driven by the endpoint's `vendors[]` array.
 *
 * Design notes:
 *  - Uses shadcn Sheet (right-side drawer) so it doesn't cover the underlying
 *    search results — user can toggle it open/closed while comparing items.
 *  - Fetches ONLY when opened. `useEffect([item])` re-runs whenever the
 *    parent selects a new item.
 *  - Client-side pagination via a simple "Load more" button. Server returns
 *    `total` so we know when to stop.
 *  - Zero-based `offset` matches server contract.
 */
import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Loader2, Package, Calendar, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 2 }).format(Number(n) || 0);

const inrShort = (n) => {
  // For the "spend" column in the table — collapses long numbers like
  // ₹2,33,380 → ₹2.33L so rows stay compact on mobile.
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100_000)    return `₹${(v / 100_000).toFixed(2)}L`;
  if (Math.abs(v) >= 1_000)      return `₹${(v / 1_000).toFixed(1)}K`;
  return inr(v);
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso)
    : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const PAGE_SIZE = 50;

export function PriceHistoryDrawer({ item, onClose }) {
  const isOpen = !!item;
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [iname, setIname] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pcode, setPcode] = useState("");  // vendor filter

  // Reset state whenever the parent selects a new item (or closes).
  // Without this, opening item B right after item A would show A's rows
  // for a moment while B's fetch is in flight. When the parent passes
  // an initial `pcode` on `item` (from the Supplier-compare card's
  // "View bills →" link), seed the filter with it so the drawer opens
  // pre-filtered to that vendor instead of showing every supplier.
  useEffect(() => {
    setRows([]);
    setVendors([]);
    setTotal(0);
    setIname("");
    setError("");
    setPcode(item?.pcode || "");
  }, [item?.icode, item?.pcode]);

  const fetchPage = useCallback(async (offset) => {
    if (!item?.icode) return;
    setLoading(true);
    setError("");
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (pcode) params.pcode = pcode;
      const { data } = await api.get(
        `/price-explorer/history/${encodeURIComponent(item.icode)}`,
        { params }
      );
      setIname(data?.iname || "");
      setTotal(data?.total || 0);
      // First page replaces; subsequent pages append.
      if (offset === 0) setRows(data?.rows || []);
      else setRows((prev) => [...prev, ...(data?.rows || [])]);
      // Vendor list is stable per icode — only overwrite on first page so
      // subsequent pagination doesn't reset the filter dropdown ordering.
      if (offset === 0) setVendors(data?.vendors || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [item?.icode, pcode]);

  // Reload page 1 whenever icode OR the vendor filter changes.
  useEffect(() => {
    if (isOpen) fetchPage(0);
  }, [item?.icode, pcode, isOpen, fetchPage]);

  const hasMore = rows.length < total;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto"
                    data-testid="price-history-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package size={18} className="text-cyan-700" />
            <span className="font-mono text-cyan-800 text-base">{item?.icode}</span>
          </SheetTitle>
          <SheetDescription className="text-slate-700">
            {iname || item?.iname || "Purchase history"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {/* Vendor filter — dropdown populated from the endpoint's counts.
              Empty value = all vendors (default). */}
          {vendors.length > 1 && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Filter vendor:
              </label>
              <select
                value={pcode}
                onChange={(e) => setPcode(e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1 bg-white"
                data-testid="price-history-vendor-filter"
              >
                <option value="">All vendors ({total})</option>
                {vendors.map((v) => (
                  <option key={v.pcode} value={v.pcode}>
                    {v.supplier || v.pcode} — {v.count} bill{v.count === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Summary strip */}
          <div className="text-xs text-slate-600" data-testid="price-history-summary">
            Showing <b>{rows.length}</b> of <b>{total}</b>{" "}
            billed line{total === 1 ? "" : "s"}
            {pcode && vendors.length > 0 && (
              <> for <b>{vendors.find(v => v.pcode === pcode)?.supplier || pcode}</b></>
            )}
          </div>

          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
              {error}
            </div>
          )}

          {loading && rows.length === 0 && (
            <div className="text-center py-12 text-slate-500" data-testid="price-history-loading">
              <Loader2 size={24} className="animate-spin mx-auto mb-2 opacity-60" />
              Loading history…
            </div>
          )}

          {!loading && rows.length === 0 && !error && (
            <div className="text-center py-12 text-slate-500 italic">
              No billed purchases found.
            </div>
          )}

          {/* Rows table — mobile: stacks; desktop: aligned columns */}
          {rows.length > 0 && (
            <div className="overflow-hidden border border-slate-200 rounded">
              <table className="w-full text-sm" data-testid="price-history-table">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Date</th>
                    <th className="text-left px-3 py-2 font-semibold">Vendor</th>
                    <th className="text-right px-3 py-2 font-semibold">Rate</th>
                    <th className="text-right px-3 py-2 font-semibold">Qty</th>
                    <th className="text-right px-3 py-2 font-semibold">Spend</th>
                    <th className="text-right px-3 py-2 font-semibold">PO#</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.purc_no}-${i}`}
                        className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                        data-testid={`price-history-row-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <Calendar size={12} className="opacity-50" />
                          {fmtDate(r.bill_date)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-800">{r.supplier || "—"}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{r.pcode}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {inr(r.rate_inr)}
                        {r.currency && r.currency !== "INR" && (
                          <div className="text-[10px] text-slate-500">
                            {r.currency} {r.rate.toFixed(2)} @ {r.exch_rate}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-700">
                        {r.qty.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        {r.unit && <span className="text-[10px] text-slate-500 ml-1">{r.unit}</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-700 font-semibold">
                        {inrShort(r.spend_inr)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">
                        {r.purc_no}
                        {r.pr_link ? (
                          <div className="text-[10px] text-slate-500">
                            PR #{r.pr_link}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center pt-2 pb-6">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fetchPage(rows.length)}
                disabled={loading}
                data-testid="price-history-load-more"
              >
                {loading ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" /> Loading…</>
                ) : (
                  <>Load {Math.min(PAGE_SIZE, total - rows.length)} more of {total - rows.length} remaining</>
                )}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default PriceHistoryDrawer;
